// Business-Central-style top module bar for the dashboard. Each main module is
// a click-to-expand button that opens a mega-menu of its groups (with icons) and
// the pages inside each group. Sits above the Activities section. Purely
// presentational — the caller passes an already permission-filtered module list.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Popover, Typography, Divider, ListItemButton,
} from '@mui/material';
import {
  ExpandMore, ChevronRight, AccountBalance, People, Business,
  AccountBalanceWallet, HourglassBottom, Inventory2, Tune, School,
  EventNote, BeachAccess, WorkspacePremium, Gavel, Calculate, Dashboard,
  ShoppingCart, Store, Storefront, Security, GridView, Home,
} from '@mui/icons-material';

// Group-title → icon. Keeps the mega-menu visually scannable like BC's ribbon.
const GROUP_ICON = {
  // Finance
  'General Ledger': AccountBalance, 'Receivables': People, 'Payables': Business,
  'Banks & Cash': AccountBalanceWallet, 'Fixed Assets': HourglassBottom,
  'Stores & Inventory': Inventory2, 'Setups': Tune,
  // Academics
  'Overview': Dashboard, 'Structure': School, 'Assessment': EventNote,
  'Fees & Charges': Business, 'Events': EventNote, 'Class Teacher': School,
  // HR
  'Employees': People, 'Leave': BeachAccess, 'Performance': WorkspacePremium,
  'Disciplinary & Exit': Gavel,
  // Payroll
  'Processing': Calculate, 'Reports': EventNote, 'Setup': Tune,
  // Supply chain
  'Purchasing': ShoppingCart, 'Inventory': Inventory2, 'Stores': Store,
  // Setups
  'Company & System': Storefront, 'Posting': Tune, 'Users & Security': Security,
  'Data': GridView,
};

export default function TopModuleBar({ modules = [] }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [active, setActive] = useState(null); // the open module

  const open = (e, mod) => { setAnchorEl(e.currentTarget); setActive(mod); };
  const close = () => { setAnchorEl(null); setActive(null); };
  const go = (path) => { close(); navigate(path); };

  if (!modules.length) return null;

  return (
    <Box sx={{
      mb: 3, display: 'flex', alignItems: 'stretch', gap: 1.5, flexWrap: 'wrap',
    }}>
      {modules.map(mod => {
        const isOpen = active?.key === mod.key;
        const Icon = mod.Icon || GridView;
        return (
          <Button
            key={mod.key}
            onClick={(e) => open(e, mod)}
            endIcon={<ExpandMore sx={{
              transition: 'transform .15s', transform: isOpen ? 'rotate(180deg)' : 'none',
            }} />}
            sx={{
              // Even, full-width distribution — each module is its own dropdown box.
              flex: '1 1 165px', minWidth: 150, justifyContent: 'space-between',
              textTransform: 'none', fontWeight: 700, fontSize: 14.5,
              color: 'text.primary', px: 1.75, py: 1.25, borderRadius: 2,
              border: '1.5px solid',
              borderColor: isOpen ? mod.color : '#d5dbe1',
              bgcolor: isOpen ? `${mod.color}0f` : '#fff',
              boxShadow: isOpen ? 3 : 1,
              '&:hover': { borderColor: mod.color, bgcolor: `${mod.color}0a`, boxShadow: 2 },
            }}>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Icon sx={{ color: mod.color, fontSize: 20 }} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {mod.title}
              </Box>
            </Box>
          </Button>
        );
      })}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: 2, boxShadow: 6, maxWidth: 760 } } }}>
        {active && (
          <Box sx={{ p: 2 }}>
            {/* Mega-menu header — module name + jump to its home dashboard */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <active.Icon sx={{ color: active.color }} />
              <Typography fontWeight={800} sx={{ flex: 1 }}>{active.title}</Typography>
              <Button size="small" startIcon={<Home fontSize="small" />}
                onClick={() => go(active.home)}
                sx={{ textTransform: 'none', color: active.color, fontWeight: 700 }}>
                Open {active.title}
              </Button>
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            {/* Groups laid out in columns, BC-ribbon style */}
            <Box sx={{
              display: 'grid', gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(210px, 1fr))', md: 'repeat(3, minmax(210px, 1fr))' },
            }}>
              {active.groups.map(group => {
                const GIcon = GROUP_ICON[group.title] || ChevronRight;
                return (
                  <Box key={group.title}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: `${group.color}1a`,
                      }}>
                        <GIcon sx={{ fontSize: 15, color: group.color }} />
                      </Box>
                      <Typography sx={{
                        fontWeight: 800, fontSize: 11.5, textTransform: 'uppercase',
                        letterSpacing: 0.4, color: group.color,
                      }}>
                        {group.title}
                      </Typography>
                    </Box>
                    {group.items.map(item => (
                      <ListItemButton key={item.path} onClick={() => go(item.path)}
                        sx={{
                          py: 0.35, pl: 4, borderRadius: 1, minHeight: 0,
                          '&:hover': { bgcolor: `${group.color}12` },
                        }}>
                        <ChevronRight sx={{ fontSize: 14, color: 'text.disabled', mr: 0.5 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{item.label}</Typography>
                      </ListItemButton>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
}
